import type { Plugin, Connect, ViteDevServer } from 'vite'
import type { ServerResponse } from 'http'
import path from 'path'
import fs from 'fs'
import crypto from 'crypto'
import { loadRundotLiveOpsConfig } from './configMerger'
import { RUNDOT_LOOKUP_ORDER } from './rundotPaths'
import {
  EMBEDDED_LIBRARY_BY_KEY,
  EMBEDDED_LIBRARIES,
  MODULE_TO_LIBRARY_SPECIFIERS,
  DEFAULT_SHARED_LIB_CDN_BASE,
  type EmbeddedLibraryDefinition,
  type ModuleSpecifierConfig,
} from '../shared-assets/embeddedLibrariesManifest'
import {
  EMBEDDED_THREE_170_NAMED_EXPORTS,
} from '../shared-assets/generated/threeExports170'
import {
  EMBEDDED_THREE_183_NAMED_EXPORTS,
} from '../shared-assets/generated/threeExports183'
import {
  EMBEDDED_REACT_18_NAMED_EXPORTS,
} from '../shared-assets/generated/reactExports18'
import {
  EMBEDDED_REACT_19_NAMED_EXPORTS,
} from '../shared-assets/generated/reactExports19'
import {
  EMBEDDED_REACT_DOM_18_NAMED_EXPORTS,
} from '../shared-assets/generated/reactDomExports18'
import {
  EMBEDDED_REACT_DOM_19_NAMED_EXPORTS,
} from '../shared-assets/generated/reactDomExports19'
import {
  EMBEDDED_INKJS_22_NAMED_EXPORTS,
} from '../shared-assets/generated/inkjsExports22'
import {
  EMBEDDED_INKJS_23_NAMED_EXPORTS,
} from '../shared-assets/generated/inkjsExports23'
import {
  EMBEDDED_PIXI_819_NAMED_EXPORTS,
} from '../shared-assets/generated/pixiExports819'
import {
  EMBEDDED_ZUSTAND_NAMED_EXPORTS,
  EMBEDDED_ZUSTAND_MIDDLEWARE_NAMED_EXPORTS,
} from '../shared-assets/generated/zustandExports'

import {
  scanForBlockedStorage,
  formatStorageViolationError,
} from './storageLinter'
import {
  getResolvedStorageCheckSeverity,
  type StorageCheckPluginOptions,
} from './storageCheckPlugin'

// --- Exports registry: keyed by "packageName:version" or "packageName:version:importPath" ---

const EMBEDDED_EXPORTS_REGISTRY: Record<string, readonly string[]> = {
  // Three.js
  'three:0.170.0': EMBEDDED_THREE_170_NAMED_EXPORTS,
  'three:0.183.2': EMBEDDED_THREE_183_NAMED_EXPORTS,
  // inkjs
  'inkjs:2.2.0': EMBEDDED_INKJS_22_NAMED_EXPORTS,
  'inkjs:2.3.2': EMBEDDED_INKJS_23_NAMED_EXPORTS,
  // React 18
  'react:18.3.1': EMBEDDED_REACT_18_NAMED_EXPORTS,
  'react-dom:18.3.1': EMBEDDED_REACT_DOM_18_NAMED_EXPORTS,
  // React 19
  'react:19.2.4': EMBEDDED_REACT_19_NAMED_EXPORTS,
  'react-dom:19.2.4': EMBEDDED_REACT_DOM_19_NAMED_EXPORTS,
  // Pixi.js
  'pixi.js:8.19.0': EMBEDDED_PIXI_819_NAMED_EXPORTS,
  // Zustand (main + middleware have different export lists)
  'zustand:5.0.3': EMBEDDED_ZUSTAND_NAMED_EXPORTS,
  'zustand:5.0.3:zustand/middleware': EMBEDDED_ZUSTAND_MIDDLEWARE_NAMED_EXPORTS,
}

const GENERATE_SCRIPT_BY_PACKAGE: Record<string, string> = {
  three: 'generate:embedded-three-exports',
  react: 'generate:embedded-react-exports',
  'react-dom': 'generate:embedded-react-exports',
  inkjs: 'generate:embedded-inkjs-exports',
  zustand: 'generate:embedded-zustand-exports',
  'pixi.js': 'generate:embedded-pixi-exports',
}

export interface RundotGameLibrariesPluginOptions {
  /**
   * Your app name (used for CDN asset serving in dev mode)
   * Optional - only needed if you have game assets in cdn/ folder
   * Example: 'block-breakers', 'fortunes-22'
   */
  appName?: string

  /**
   * Path to local cdn/ folder (relative to vite.config.ts)
   * Defaults to './cdn'
   */
  cdnDir?: string

  /**
   * Enable debug logging
   * Defaults to false
   */
  debug?: boolean

  /**
   * Blocked storage API checks (localStorage, indexedDB, sessionStorage).
   * Enabled by default to catch blocked browser storage APIs early in dev and build.
   * Pass `false` or `{ enabled: false }` to disable, or `{ severity: 'warn' }` to warn only.
   */
  storageCheck?: boolean | StorageCheckPluginOptions

  /**
   * Target max devicePixelRatio for clamping on high-DPI displays.
   * Defaults to reading from game.config.prod.json (maxDpr, default 1.5).
   * Pass a number (e.g. 1.5, 2.0) to override, or `false` to disable.
   */
  maxDpr?: number | false
}

/**
 * Generate virtual module code based on library and specifier behavior
 */
async function resolveInstalledPackageVersion(
  pluginContext: { resolve: Plugin['resolveId'] extends any ? any : any },
  packageName: string,
  cache: Map<string, string | null>,
): Promise<string | null> {
  if (cache.has(packageName)) {
    return cache.get(packageName) ?? null
  }

  const readVersion = (packageJsonPath: string): string | null => {
    const raw = fs.readFileSync(packageJsonPath, 'utf8')
    const parsed = JSON.parse(raw)
    return typeof parsed?.version === 'string' ? parsed.version : null
  }

  const findNearestPackageJson = (fromFilePath: string): string | null => {
    let dir = path.dirname(fromFilePath)
    while (true) {
      const candidate = path.join(dir, 'package.json')
      if (fs.existsSync(candidate) && fs.statSync(candidate).isFile()) {
        return candidate
      }
      const parent = path.dirname(dir)
      if (parent === dir) return null
      dir = parent
    }
  }

  try {
    const pkgJsonResolved = await pluginContext.resolve(
      `${packageName}/package.json`,
      void 0,
      { skipSelf: true },
    )
    if (pkgJsonResolved?.id && fs.existsSync(pkgJsonResolved.id)) {
      const v = readVersion(pkgJsonResolved.id)
      cache.set(packageName, v)
      return v
    }
  } catch {
    // exports map may block this subpath, or package.json is unreadable — fall through
  }

  try {
    const entryResolved = await pluginContext.resolve(packageName, void 0, {
      skipSelf: true,
    })
    if (entryResolved?.id) {
      const pkgJsonPath = findNearestPackageJson(entryResolved.id)
      const v = pkgJsonPath ? readVersion(pkgJsonPath) : null
      cache.set(packageName, v)
      return v
    }
  } catch {
    // entry is not resolvable, or nearest package.json is unreadable
  }

  cache.set(packageName, null)
  return null
}

function generateVirtualModule(
  lib: EmbeddedLibraryDefinition,
  spec: ModuleSpecifierConfig,
  importPath: string,
): string {
  const errorCheck = `
// Wait for shim to complete loading libraries
if (window.__rundotGameLibraryShim && window.__rundotGameLibraryShim.ready) {
  try {
    await window.__rundotGameLibraryShim.ready();
  } catch (error) {
    console.error('[RUN:libraries] Shim bootstrap failed:', error);
    throw error;
  }
} else {
  console.warn('[RUN:libraries] No shim found - running without embedded libraries');
}

// Verify library was loaded
if (!window.__rundotGameLibraryExports || !window.__rundotGameLibraryExports['${lib.libraryKey}']) {
  console.error('[RUN:libraries] ${lib.packageName} library not loaded');
  console.error('[RUN:libraries] Config:', window.__rundotGameLibrariesConfig);
  console.error('[RUN:libraries] Exports:', window.__rundotGameLibraryExports);
  throw new Error(
    '${lib.packageName} not loaded (${importPath}). Check console for errors.'
  );
}
`

  const libVar = `window.__rundotGameLibraryExports['${lib.libraryKey}']`

  const renderForwardedNamedExports = (
    names: readonly string[],
    chunkSize = 50,
  ): string => {
    if (names.length === 0) return ''

    const chunks: string[] = []
    for (let i = 0; i < names.length; i += chunkSize) {
      const slice = names.slice(i, i + chunkSize).join(', ')
      chunks.push(`export const { ${slice} } = lib;`)
    }
    return `\n${chunks.join('\n')}\n`
  }

  switch (spec.behavior) {
    case 'react-jsx-runtime':
      return `
const React = await (async function() {
  ${errorCheck}
  return ${libVar};
})();

export const jsx = React.jsx || function(type, props, key) {
  return React.createElement(type, { ...props, key });
};
export const jsxs = React.jsxs || jsx;
export const Fragment = React.Fragment;
`

    case 'react-jsx-dev-runtime':
      return `
const React = await (async function() {
  ${errorCheck}
  return ${libVar};
})();

export const jsxDEV = React.jsxDEV || React.createElement;
export const Fragment = React.Fragment;
`

    case 'namespace': {
      // Registry-based lookup: try sub-path key first, then base key
      const subpathKey = `${lib.packageName}:${lib.version}:${importPath}`
      const baseKey = `${lib.packageName}:${lib.version}`
      const exports = EMBEDDED_EXPORTS_REGISTRY[subpathKey]
        ?? EMBEDDED_EXPORTS_REGISTRY[baseKey]

      let namedExports = ''
      if (!exports) {
        const scriptHint = GENERATE_SCRIPT_BY_PACKAGE[lib.packageName] ?? 'unknown'
        namedExports = `
throw new Error('[RUN:libraries] No generated exports for ${subpathKey} or ${baseKey}. Run: npm run ${scriptHint} -- --version ${lib.version} (packages/api)');`
      } else {
        namedExports = renderForwardedNamedExports(exports)
      }

      return `
const lib = await (async function() {
  ${errorCheck}
  return ${libVar};
})();

export default lib;
${namedExports}
`
    }

    case 'default':
    case undefined:
      return `
const lib = await (async function() {
  ${errorCheck}
  return ${libVar};
})();

export default lib;
`

    default:
      throw new Error(
        `Unknown behavior: ${spec.behavior} for ${lib.packageName}. Valid behaviors: default, namespace, react-jsx-runtime, react-jsx-dev-runtime`,
      )
  }
}

/**
 * Vite plugin for RUN.world embedded libraries system.
 *
 * This plugin externalizes supported libraries (Phaser, React, Three.js, etc.)
 * and loads them from the RUN.world host on mobile or CDN on web.
 *
 * By default, libraries are embedded for optimal bundle size.
 * Set RUN.world_DISABLE_EMBEDDED_LIBS=true to bundle libraries for standalone export.
 *
 * @example
 * ```typescript
 * // vite.config.ts
 * import { rundotGameLibrariesPlugin } from '@series-inc/rundot-game-sdk/vite';
 *
 * export default defineConfig({
 *   plugins: [\n *     rundotGameLibrariesPlugin({\n *       appName: 'my-game',\n *       cdnDir: './cdn',\n *     }),\n *   ],\n * });\n * ```\n *\n * Default build: npm run build (embedded libraries)\n * Standalone export: RUN.world_DISABLE_EMBEDDED_LIBS=true npm run build\n */
/** True for a `rundot/liveops.config.json` (or legacy `.rundot/`) path (slash-normalized). */
function isLiveOpsConfigPath(p: string): boolean {
  const normalized = p.replace(/\\/g, '/')
  return RUNDOT_LOOKUP_ORDER.some((dir) => normalized.endsWith(`/${dir}/liveops.config.json`))
}

/**
 * Prepend a head <script> seeding `window.__RUNDOT_GAME_MOCK__.liveops` from the
 * on-disk rundot/liveops.config.json. The bootstrap merges this global into
 * `_mock`, and MockHost serves it through MockLiveOpsApi — so mock mode resolves
 * your real LiveOps config with no backend. A malformed file logs (not fatal); the
 * game boots with empty config and reloads once you fix it.
 */
function injectMockLiveOps(html: string, projectRoot: string): string {
  let liveops: Record<string, unknown> | null = null
  try {
    liveops = loadRundotLiveOpsConfig(projectRoot)
  } catch (err) {
    console.error(`[RUN:mock] LiveOps config error: ${err instanceof Error ? err.message : String(err)}`)
  }
  const mockData = liveops ? { liveops } : {}
  // Merge (don't clobber) so programmatic overrides set before load survive.
  const script =
    `<script type="module">window.__RUNDOT_GAME_MOCK__ = ` +
    `Object.assign(window.__RUNDOT_GAME_MOCK__ || {}, ${JSON.stringify(mockData)});</script>`
  return html.replace('<head>', '<head>' + script)
}

/**
 * Resolves the configured maxDpr for the project.
 * Checks options.maxDpr, then game.config.prod.json, game.config.json,
 * game.config.local.json, and config.json. Defaults to 1.5 if omitted.
 */
export function resolveConfigMaxDpr(
  projectRoot: string,
  optionMaxDpr?: number | false,
): number | null {
  if (typeof optionMaxDpr === 'number') {
    return optionMaxDpr > 0 ? optionMaxDpr : null
  }
  if (optionMaxDpr === false) {
    return null
  }
  const candidates = [
    'game.config.prod.json',
    'game.config.json',
    'game.config.local.json',
    'config.json',
  ]
  for (const candidate of candidates) {
    const filePath = path.join(projectRoot, candidate)
    if (fs.existsSync(filePath)) {
      try {
        const raw = fs.readFileSync(filePath, 'utf-8')
        const parsed = JSON.parse(raw)
        if (typeof parsed?.maxDpr === 'number') {
          return parsed.maxDpr > 0 ? parsed.maxDpr : null
        }
        if (parsed?.maxDpr === false) {
          return null
        }
        return 1.5
      } catch {
        return 1.5
      }
    }
  }
  return 1.5
}

export function rundotGameLibrariesPlugin(
  options: RundotGameLibrariesPluginOptions = {},
): Plugin {
  const cdnDir = path.resolve(options.cdnDir || './cdn')
  const debug = options.debug || false
  const requiredLibraries = new Set<string>()
  let isServe = false
  let enabled = false
  // Serve-mode mock LiveOps: inject rundot/liveops.config.json for MockHost when no
  // playground plugin is present (see configResolved). Independent of `enabled`,
  // which gates the build-only embedded-libraries behavior.
  let mockLiveOpsEnabled = false
  let projectRoot = process.cwd()
  let appName = options.appName
  const installedVersionCache = new Map<string, string | null>()
  const versionProbeWarned = new Set<string>()
  let bootstrapAssetPath: string | null = null
  let resolvedMaxDpr: number | null = null

  const storageCheckOpts =
    typeof options.storageCheck === 'object' ? options.storageCheck : {}
  const storageCheckExplicitlyDisabled =
    options.storageCheck === false || storageCheckOpts.enabled === false
  const storageSeverity = storageCheckExplicitlyDisabled
    ? 'off'
    : getResolvedStorageCheckSeverity(storageCheckOpts.severity)
  const storageInclude =
    storageCheckOpts.include || /\.(?:[jt]sx?|m[jt]s|c[jt]s|vue|svelte)$/i
  const storageExclude =
    storageCheckOpts.exclude || /(?:[\\/]node_modules[\\/]|[\\/]packages[\\/]sdk[\\/])/

  return {
    name: 'vite-plugin-rundot-game-libraries',
    enforce: 'pre',

    transform(code: string, id: string) {
      if (storageSeverity === 'off') return null
      if (id.startsWith('\0') || id.startsWith('virtual:')) return null
      if (storageExclude.test(id) || !storageInclude.test(id)) return null

      const violations = scanForBlockedStorage(code, id, storageCheckOpts)
      if (violations.length === 0) return null

      const formattedMessages = violations.map((v) =>
        formatStorageViolationError(v, code, id),
      )
      const combinedMessage = formattedMessages.join('\n\n')

      if (storageSeverity === 'warn') {
        console.warn(`\n${combinedMessage}\n`)
        return null
      }

      throw new Error(`\n${combinedMessage}\n`)
    },

    async buildStart() {
      if (!enabled) return
      const uniquePackages = Array.from(
        new Set(
          EMBEDDED_LIBRARIES.filter((l) => l.enabled).map((l) => l.packageName),
        ),
      )
      for (const pkgName of uniquePackages) {
        await resolveInstalledPackageVersion(this, pkgName, installedVersionCache)
      }
    },

    config(config: any): void {
      if (!enabled) return

      if (!config.build) config.build = {}
      if (!config.build.rollupOptions) config.build.rollupOptions = {}

      if (config.build.rollupOptions.output) {
        const output = config.build.rollupOptions.output
        const outputs = Array.isArray(output) ? output : [output]

        outputs.forEach((o) => {
          if (o.manualChunks && typeof o.manualChunks === 'object') {
            const chunks = o.manualChunks as Record<string, string[]>
            EMBEDDED_LIBRARIES.forEach((lib) => {
              if (lib.enabled && chunks[lib.packageName]) {
                delete chunks[lib.packageName]
              }
            })
          }
        })
      }
    },

    configResolved(config: any): void {
      isServe = config.command === 'serve'
      enabled = !isServe && process.env.RUNDOT_GAME_DISABLE_EMBEDDED_LIBS !== 'true'

      projectRoot = config.root || process.cwd()
      resolvedMaxDpr = resolveConfigMaxDpr(projectRoot, options.maxDpr)

      // Auto-load rundot/liveops.config.json into MockHost ONLY when the playground
      // plugin is absent — i.e. the SDK runs MockHost, not PlaygroundHost. With the
      // playground plugin present, LiveOps goes through its normal deploy/HMR path
      // and injecting here would just be dead (PlaygroundHost ignores _mock).
      const hasPlaygroundPlugin =
        Array.isArray(config.plugins) &&
        config.plugins.some((p: { name?: string }) => p?.name === 'rundot-game-sandbox')
      mockLiveOpsEnabled = isServe && !hasPlaygroundPlugin

      if (!appName && config.root) {
        const rootParts = config.root.split(path.sep)
        const h5Index = rootParts.indexOf('H5')
        if (h5Index >= 0 && h5Index < rootParts.length - 1) {
          appName = rootParts[h5Index + 1]
        }
      }

      if (isServe) {
        return
      }

    },

    // Multi-version resolveId: iterate ALL matches and check version gate for each
    async resolveId(
      id: string,
      _importer: string | undefined,
    ): Promise<string | null> {
      if (!enabled) return null

      const allMatches = MODULE_TO_LIBRARY_SPECIFIERS.filter((spec) => {
        if (spec.match === 'exact') return spec.value === id
        if (spec.match === 'prefix') return id.startsWith(spec.value)
        return false
      })

      for (const match of allMatches) {
        const lib = EMBEDDED_LIBRARY_BY_KEY[match.libraryKey]
        if (!lib) continue

        const installed = await resolveInstalledPackageVersion(
          this,
          lib.packageName,
          installedVersionCache,
        )

        if (installed === null) {
          if (!versionProbeWarned.has(lib.packageName)) {
            versionProbeWarned.add(lib.packageName)
            this.warn(
              `[RUN:libraries] Could not determine the installed version of "${lib.packageName}". It will be bundled instead of served by the host.`,
            )
          }
          continue
        }

        if (installed === lib.version) {
          requiredLibraries.add(match.libraryKey)
          return `\0virtual:rundot-game-lib:${match.libraryKey}:${id}`
        }
      }

      return null
    },

    load(id: string): string | null {
      if (!id.startsWith('\0virtual:rundot-game-lib:')) {
        return null
      }

      const parts = id.split(':')
      const libraryKey = parts[2]
      const importPath = parts.slice(3).join(':')

      const lib = EMBEDDED_LIBRARY_BY_KEY[libraryKey]
      if (!lib) {
        const availableKeys = Object.keys(EMBEDDED_LIBRARY_BY_KEY).join(', ')
        throw new Error(
          `Unknown library: ${libraryKey}. Available libraries: ${availableKeys}`,
        )
      }

      const spec = lib.moduleSpecifiers.find((s) => {
        if (s.match === 'exact') return s.value === importPath
        if (s.match === 'prefix') return importPath.startsWith(s.value)
        return false
      })
      if (!spec) {
        const validSpecifiers = lib.moduleSpecifiers
          .map((s) => s.value)
          .join(', ')
        throw new Error(
          `Unknown module specifier: ${importPath} for ${libraryKey}. Valid specifiers: ${validSpecifiers}`,
        )
      }

      const virtualModule = generateVirtualModule(lib, spec, importPath)

      return `// Virtual module: ${importPath}\n${virtualModule}`
    },

    transformIndexHtml(html: string): string {
      // Serve-mode mock LiveOps runs independently of the build-only `enabled` gate.
      if (mockLiveOpsEnabled) {
        html = injectMockLiveOps(html, projectRoot)
      }
      if (!enabled || (requiredLibraries.size === 0 && resolvedMaxDpr === null) || !bootstrapAssetPath) {
        return html
      }
      // R13c: external bootstrap (file emitted in generateBundle) instead of
      // inline <script>. CSP `script-src 'self'` accepts this; inline blocks
      // are rejected post-lockdown.
      const tag = `<script src="./${bootstrapAssetPath}" defer></script>`
      return html.replace('<head>', '<head>' + tag)
    },

    configureServer(server: ViteDevServer): void {
      // Reload the page when rundot/liveops.config.json is created or edited, so
      // the re-run of transformIndexHtml re-injects the fresh mock config. Uses
      // Vite's built-in full-reload — MockHost reads live, no cache to clear.
      if (mockLiveOpsEnabled) {
        for (const dir of RUNDOT_LOOKUP_ORDER) {
          server.watcher.add(path.join(projectRoot, dir, 'liveops.config.json').replace(/\\/g, '/'))
        }
        let reloadTimer: ReturnType<typeof setTimeout> | null = null
        const onLiveOpsChange = (changed: string): void => {
          if (!isLiveOpsConfigPath(changed)) return
          if (reloadTimer) clearTimeout(reloadTimer)
          reloadTimer = setTimeout(() => server.ws.send({ type: 'full-reload' }), 150)
        }
        server.watcher.on('add', onLiveOpsChange)
        server.watcher.on('change', onLiveOpsChange)
      }

      if (!enabled || !appName) {
        return
      }

      server.middlewares.use(
        (
          req: Connect.IncomingMessage,
          res: ServerResponse,
          next: Connect.NextFunction,
        ) => {
          if (!req.url) return next()

          const urlPath = req.url.split('?')[0]
          if (!urlPath.startsWith(`/${appName}/`)) return next()

          const relativePath = urlPath.replace(`/${appName}/`, '')
          const filePath = path.join(cdnDir, relativePath)

          if (fs.existsSync(filePath) && fs.statSync(filePath).isFile()) {
            const ext = path.extname(filePath).toLowerCase()
            const contentTypes: Record<string, string> = {
              '.png': 'image/png',
              '.jpg': 'image/jpeg',
              '.jpeg': 'image/jpeg',
              '.gif': 'image/gif',
              '.webp': 'image/webp',
              '.mp3': 'audio/mpeg',
              '.mp4': 'video/mp4',
              '.ogg': 'audio/ogg',
              '.wav': 'audio/wav',
              '.json': 'application/json',
            }

            res.setHeader(
              'Content-Type',
              contentTypes[ext] || 'application/octet-stream',
            )
            res.setHeader('Cache-Control', 'public, max-age=3600')
            return res.end(fs.readFileSync(filePath))
          }

          next()
        },
      )
    },

    generateBundle(): void {
      if (!enabled || (requiredLibraries.size === 0 && resolvedMaxDpr === null)) return

      const libraries: Record<string, any> = {}
      const bootstrapManifest: Record<string, BootstrapManifestEntry> = {}

      requiredLibraries.forEach((libraryKey) => {
        const lib = EMBEDDED_LIBRARY_BY_KEY[libraryKey]
        libraries[libraryKey] = {
          assetKey: lib.assetKey,
          cdnPath: lib.cdnPath,
          globalVar: lib.globalVar,
          packageName: lib.packageName,
          version: lib.version,
          moduleSpecifiers: lib.moduleSpecifiers.map((s) => s.value),
        }
        bootstrapManifest[libraryKey] = {
          assetKey: lib.assetKey,
          cdnPath: lib.cdnPath,
          globalVar: lib.globalVar,
          loadStage: lib.loadStage,
          sha384: lib.sha384,
        }
      })

      if (requiredLibraries.size > 0) {
        const manifest = {
          enabled: true,
          required: Array.from(requiredLibraries),
          libraries,
          cdnBase: DEFAULT_SHARED_LIB_CDN_BASE,
          buildTime: new Date().toISOString(),
          plugin: '@series-inc/rundot-game-sdk/vite',
        }

        this.emitFile({
          type: 'asset',
          fileName: 'rundot-game-libraries.manifest.json',
          source: JSON.stringify(manifest, null, 2),
        })
      }

      // R13c + R13b: emit external bootstrap loader. Replaces the inline
      // <script> block (CSP `script-src 'self'` rejects inline) and the
      // fetch + `new Function(src)` evaluator
      // with a `<script src>` injection that carries an SRI integrity
      // attribute pinned in the manifest.
      const bootstrapSource = generateBootstrapSource({
        required: Array.from(requiredLibraries),
        manifest: bootstrapManifest,
        cdnBase: DEFAULT_SHARED_LIB_CDN_BASE,
        debug,
        configMaxDpr: resolvedMaxDpr,
      })
      const hash = crypto
        .createHash('sha256')
        .update(bootstrapSource)
        .digest('hex')
        .slice(0, 8)
      bootstrapAssetPath = `assets/_rundot_bootstrap.${hash}.js`
      this.emitFile({
        type: 'asset',
        fileName: bootstrapAssetPath,
        source: bootstrapSource,
      })
    },
  }
}

interface BootstrapManifestEntry {
  assetKey: string
  cdnPath: string
  globalVar: string
  loadStage: number
  sha384: string
}

interface GenerateBootstrapOptions {
  required: string[]
  manifest: Record<string, BootstrapManifestEntry>
  cdnBase: string
  debug: boolean
  configMaxDpr?: number | null
}

/**
 * Bootstrap script content. Runs in the H5 game iframe BEFORE the game's
 * main module evaluates. Loads each required shared library from the
 * configured CDN via `<script src>` injection (with SRI from the manifest)
 * and exposes the globals via `window.__rundotGameLibraryExports`.
 *
 * R13c: emitted as an external file, so the game's index.html ships
 * `<script src="./assets/_rundot_bootstrap.<hash>.js" defer>` instead of
 * an inline block — CSP `script-src 'self'` accepts that, rejects inline.
 *
 * R13b: replaces the previous `fetch(url).then(text => new Function(text)())`
 * loop so the browser can enforce the manifest's integrity hashes.
 *
 * R32c: every injected `<script>` carries `integrity` + `crossorigin`
 * attributes sourced from the manifest, so a malicious / corrupted CDN
 * payload fails SRI and never executes.
 */
export function generateBootstrapSource(opts: GenerateBootstrapOptions): string {
  return `// Generated by RundotGameLibrariesPlugin — do not edit.
;(function () {
  // Per-mount config delivery via URL query params. Read BEFORE any
  // message listener is installed (the SDK transport listener reads
  // parentOrigin to validate incoming messages — chicken-and-egg).
  try {
    var params = new URLSearchParams(location.search);
    var hashParams = new URLSearchParams(location.hash.replace(/^#/, ''));
    var cfg = {
      poolId: params.get('_pid') || '',
      nonce: hashParams.get('_nonce') || params.get('_nonce') || '',
      parentOrigin: params.get('_parent') || '',
      appId: params.get('_appId') || '',
      debug: params.get('_dbg') === '1'
    };
    window.__rundotConfig = cfg;
    if (!window.rundotGame) window.rundotGame = {};
    window.rundotGame._config = cfg;
    var maxDprParam = parseFloat(params.get('_maxDpr') || hashParams.get('_maxDpr'));
    var configMaxDpr = ${typeof opts.configMaxDpr === 'number' ? opts.configMaxDpr : 'null'};
    var maxDpr = !isNaN(maxDprParam) && maxDprParam > 0 ? maxDprParam : configMaxDpr;
    if (typeof maxDpr === 'number' && maxDpr > 0 && typeof window !== 'undefined' && window.devicePixelRatio > maxDpr) {
      try {
        Object.defineProperty(window, 'devicePixelRatio', {
          get: function() { return maxDpr; },
          configurable: true
        });
      } catch (e) {}
    }
  } catch (e) {
    if (typeof console !== 'undefined' && console.warn) {
      console.warn('[RUN:bootstrap] failed to parse query params', e);
    }
  }

  if (!window.__rundotGameLibrariesConfig) {
    window.__rundotGameLibrariesConfig = {
      enabled: true,
      required: ${JSON.stringify(opts.required)},
      manifest: ${JSON.stringify(opts.manifest)},
      cdnBase: ${JSON.stringify(opts.cdnBase)},
      useHost: false,
      debug: ${opts.debug ? 'true' : 'false'}
    };
  }

  if (window.__rundotGameLibraryShim && window.__rundotGameLibraryShim.__initialized) {
    return;
  }

  var bootstrapResolve, bootstrapReject;
  var bootstrapPromise = new Promise(function (resolve, reject) {
    bootstrapResolve = resolve;
    bootstrapReject = reject;
  });

  window.__rundotGameLibraryShim = {
    ready: function () { return bootstrapPromise; },
    __initialized: true
  };

  var config = window.__rundotGameLibrariesConfig;
  if (!config.required || config.required.length === 0) {
    bootstrapResolve();
    return;
  }

  function buildCdnUrl(entry) {
    var base = config.cdnBase || '';
    if (!base.endsWith('/')) base += '/';
    var path = entry.cdnPath;
    if (path.charAt(0) === '/') path = path.substring(1);
    return base + path;
  }

  function loadLibrary(libraryKey) {
    return new Promise(function (resolve, reject) {
      var entry = config.manifest[libraryKey];
      if (!entry) {
        reject(new Error('No manifest entry for ' + libraryKey));
        return;
      }
      var url = buildCdnUrl(entry);
      var script = document.createElement('script');
      script.src = url;
      if (entry.sha384) {
        script.integrity = entry.sha384;
        script.crossOrigin = 'anonymous';
      }
      script.onload = function () {
        var exported = window[entry.globalVar];
        if (!exported) {
          reject(new Error(libraryKey + ' loaded but window.' + entry.globalVar + ' is undefined.'));
          return;
        }
        if (!window.__rundotGameLibraryExports) window.__rundotGameLibraryExports = {};
        window.__rundotGameLibraryExports[libraryKey] = exported;
        resolve();
      };
      script.onerror = function () {
        reject(new Error('Failed to load ' + libraryKey + ' from ' + url));
      };
      document.head.appendChild(script);
    });
  }

  var librariesByStage = {};
  for (var i = 0; i < config.required.length; i++) {
    var libraryKey = config.required[i];
    var entry = config.manifest[libraryKey];
    var stage = (entry && entry.loadStage) || 0;
    if (!librariesByStage[stage]) librariesByStage[stage] = [];
    librariesByStage[stage].push(libraryKey);
  }

  var stages = Object.keys(librariesByStage).sort(function (a, b) {
    return parseInt(a, 10) - parseInt(b, 10);
  });

  (async function loadByStages() {
    for (var s = 0; s < stages.length; s++) {
      var libs = librariesByStage[stages[s]];
      await Promise.all(libs.map(loadLibrary));
    }
  })()
    .then(function () { bootstrapResolve(); })
    .catch(function (err) {
      console.error('[RUN:libraries] Library loading failed:', (err && err.message) || err);
      bootstrapReject(err);
    });
})();
`
}
